import {PIXI} from '@colonist/pixi'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {GameColor} from '../../GameColor'
import {UIGameActionButtonView} from './UIGameActionButtonView'
import {UIGameManager} from '../UIGameManager'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {TextView} from '../../ui_canvas_engine/TextView'
import {GameFont} from '../../GameFont'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'

export abstract class UIGameActionButtonViewWithBadge extends UIGameActionButtonView {
    private readonly numberBadge: SpriteView
    private count: number
    private countTextView: TextView

    constructor(uiGameManager: UIGameManager, preferredPosition: number, container: PIXI.Container, startingAmount: number) {
        super(uiGameManager, preferredPosition, container)

        const size = this.viewContainer.height / 3
        const buttonBorderX = this.viewContainer.width * 0.075
        const buttonBorderY = this.viewContainer.height * 0.04
        const badgeX = this.viewContainer.width - size
        const badgeTexture = UIGameSpriteHandler.getButtonBadgeForPlayer(this.uiGameManager.gameController.myColor)
        this.numberBadge = new SpriteView(badgeX - buttonBorderX, buttonBorderY, size, size, this.viewContainer, badgeTexture)

        this.count = startingAmount
        this.countTextView = this.createCardCountText()
        if(this.uiGameManager.gameController.isPlayerSpectator) this.removeButtonBadge()
    }

    protected abstract stateUpdated(): void
    protected abstract playerStateUpdated(): void

    buttonSpecificEvents(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.PlayerControllerStateUpdated:
                this.stateUpdated()
                this.playerStateUpdated()
                break
            case GameStateEnums.UIGameEventEnums.GameStateUpdated: this.stateUpdated(); break
            case GameStateEnums.UIGameEventEnums.BeginnerModeStart: this.removeButtonBadge(); break
            case GameStateEnums.UIGameEventEnums.CanvasResized: this.replaceBadgeText(); break
            default: break
        }
    }

    private createCardCountText(): TextView {
        const colorDarkGrey = GameColor.getColor(GameColor.Color.DarkGrey)
        const colorOffWhite = GameColor.getColor(GameColor.Color.OffWhite)

        const x = this.viewContainer.width * 0.78
        const y = this.viewContainer.height * 0.17

        const countText = new TextView({key: 'strings:colonistCommon.utils.#buffer', options: {value: String(this.count)}}, x, y, this.viewContainer.width, this.viewContainer.height, this.viewContainer, undefined, undefined, undefined, {
            fontSize: GameFont.getResponsiveSize(GameFont.Size.XXL),
            fontWeight: GameFont.Weight.Bold,
            fill: colorOffWhite,
            stroke: colorDarkGrey,
            strokeThickness: 5,
        })

        countText.position.set(x, y)
        countText.anchor.set(0.5, 0.5)

        const ratio = countText.width / countText.height
        countText.height = this.viewContainer.height * 0.26
        countText.width = countText.height * ratio

        return countText
    }

    protected updateBadgeCount(count: number) {
        if(count < 0) count = 0
        this.count = count

        if(this.countTextView.parent == undefined) return
        this.countTextView.setText({key: 'strings:colonistCommon.utils.#buffer', options: {value: String(this.count)}})

        // June 19 2023. Berke: Updating the text doesn't seem to work, so this function destroys and recreates the text. Also check here https://github.com/colonistio/katan/pull/4039#discussion_r614565205
        this.replaceBadgeText()
    }

    private replaceBadgeText() {
        this.removeNumberText()
        this.countTextView.destroy()
        this.countTextView = this.createCardCountText()
    }

    private removeButtonBadge() {
        this.removeNumberBadge()
        this.removeNumberText()
    }

    private removeNumberBadge() {
        if(this.numberBadge.parent == undefined) return
        this.numberBadge.parent.removeChild(this.numberBadge)
    }

    private removeNumberText() {
        if(this.countTextView.parent == undefined) return
        this.countTextView.parent.removeChild(this.countTextView)
    }

    protected updateOpacity(opacity: number) {
        super.updateOpacity(opacity)
        if(this.numberBadge.alpha == opacity) return
        this.numberBadge.alpha = opacity
    }

    protected onGivePlayerCardFromTile() {
        if(!this.isBeginnerMode) return
        this.updateAction(() => new FloatingText({key: 'strings:game.errors.resourcesBeingDealt'}, this))
    }
}
